import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Button, Card, Input } from '../../components/primitives';
import { AuthLayout } from './AuthLayout';
import { api } from '../../lib/api';
import { apiErrorMessage } from './authMessages';
import styles from './AuthPages.module.css';

export function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  // E-mail z backendu posílá ?reset_token=…, interní odkazy používají ?token=…
  const token = searchParams.get('token') ?? searchParams.get('reset_token');

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);

    if (password.length < 6) {
      setError('Heslo musí mít alespoň 6 znaků.');
      return;
    }
    if (password !== passwordConfirm) {
      setError('Hesla se neshodují.');
      return;
    }

    setSubmitting(true);
    try {
      await api.post('/auth/reset-password', { token, new_password: password });
      setDone(true);
    } catch (err) {
      setError(apiErrorMessage(err, 'Změna hesla se nezdařila. Odkaz může být neplatný nebo vypršel.'));
    } finally {
      setSubmitting(false);
    }
  }

  if (!token) {
    return (
      <AuthLayout subtitle="Nastavení nového hesla">
        <Card>
          <div className={styles.form}>
            <div className={styles.error} role="alert">
              Odkaz pro obnovu hesla je neúplný — chybí ověřovací token. Nechte si poslat nový.
            </div>
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/forgot-password')}>
              Poslat nový odkaz
            </Button>
            <div className={styles.switch}>
              <Link to="/login" className={styles.link}>
                ← Zpět na přihlášení
              </Link>
            </div>
          </div>
        </Card>
      </AuthLayout>
    );
  }

  if (done) {
    return (
      <AuthLayout subtitle="Nastavení nového hesla">
        <Card>
          <div className={styles.form}>
            <div className={styles.success} role="status">
              Heslo bylo změněno. Můžete se přihlásit novým heslem.
            </div>
            <Button variant="primary" size="lg" fullWidth onClick={() => navigate('/login')}>
              Přejít na přihlášení
            </Button>
          </div>
        </Card>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout subtitle="Nastavení nového hesla">
      <Card>
        <form className={styles.form} onSubmit={handleSubmit} noValidate>
          {error && (
            <div className={styles.error} role="alert">
              {error}
            </div>
          )}

          <Input
            label="Nové heslo"
            type="password"
            autoComplete="new-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            placeholder="Alespoň 6 znaků"
          />

          <Input
            label="Nové heslo znovu"
            type="password"
            autoComplete="new-password"
            required
            value={passwordConfirm}
            onChange={(e) => setPasswordConfirm(e.target.value)}
            placeholder="••••••••"
          />

          <Button type="submit" variant="primary" size="lg" fullWidth disabled={submitting}>
            {submitting ? 'Ukládám…' : 'Nastavit nové heslo'}
          </Button>

          <div className={styles.switch}>
            <Link to="/login" className={styles.link}>
              ← Zpět na přihlášení
            </Link>
          </div>
        </form>
      </Card>
    </AuthLayout>
  );
}
